import { getPSTDate, toIsoDate } from "@common/helpers/date";
import {
    SCHEDULE_SOURCE_TYPE_LABELS,
    matchesScheduleLifecycleFilterStatus,
} from "@common/helpers/domains/accounting/scheduleApplicationFilters";
import {
    ScheduleApplicationPeriodStatus,
    ScheduleApplicationStatus,
    ScheduleJeSubtype,
    ScheduleLifecycleFilterStatus,
    ScheduleLifecycleVerb,
} from "@common/types/domains/accounting/schedule";
import { StatusIndicatorVariant } from "@design-system/StatusIndicator/StatusIndicator";
import { MILESTONE_ACHIEVED_MENU_LABEL } from "./scheduleLifecycleCopy";
import {
    canMarkMilestoneAchieved,
    isMilestoneOnHold,
} from "./milestoneRowHelpers";
import {
    ScheduleApplicationPeriodRow,
    ScheduleApplicationRow,
} from "./scheduleApplicationRowHelpers";

export { ScheduleLifecycleFilterStatus, matchesScheduleLifecycleFilterStatus };

export interface ScheduleApplicationRowStatus {
    label: string;
    variant: StatusIndicatorVariant;
    tooltip: string;
}

export const SCHEDULE_ACTIVE_STATUS: ScheduleApplicationRowStatus = {
    label: "Active",
    variant: "blue",
    tooltip: "Running as applied. Nothing has been edited.",
};

export const SCHEDULE_MODIFIED_STATUS: ScheduleApplicationRowStatus = {
    label: "Changed",
    variant: "blue",
    tooltip: "Still running. The term, dates, or amount were edited.",
};

const APPLICATION_STATUS_DISPLAY: Partial<
    Record<ScheduleApplicationStatus, ScheduleApplicationRowStatus>
> = {
    [ScheduleApplicationStatus.draft]: {
        label: "Draft",
        variant: "gray",
        tooltip: "Not applied yet. No journal entries.",
    },
    [ScheduleApplicationStatus.paused]: {
        label: "Paused",
        variant: "yellow",
        tooltip:
            "Recognition is on hold. Future entries were removed and come back on resume.",
    },
    [ScheduleApplicationStatus.stopped]: {
        label: "Stopped",
        variant: "gray",
        tooltip: "Recognition ended early. Future entries are gone.",
    },
    [ScheduleApplicationStatus.unwound]: {
        label: "Removed",
        variant: "red",
        tooltip: "The schedule was deleted. Earned entries were reversed.",
    },
    [ScheduleApplicationStatus.accelerated]: {
        label: "Accelerated",
        variant: "purple",
        tooltip: "The remainder was recognized on the stop date.",
    },
    [ScheduleApplicationStatus.released]: {
        label: "Released",
        variant: "purple",
        tooltip: "The remainder was moved to another account.",
    },
    [ScheduleApplicationStatus.completed]: {
        label: "Completed",
        variant: "blue",
        tooltip: "Every month has been recognized.",
    },
    [ScheduleApplicationStatus.disposed]: {
        label: "Disposed",
        variant: "gray",
        tooltip: "The asset was disposed.",
    },
};

const PERIOD_STATUS_DISPLAY: Record<
    ScheduleApplicationPeriodStatus,
    ScheduleApplicationRowStatus
> = {
    [ScheduleApplicationPeriodStatus.posted]: {
        label: "Posted",
        variant: "gray",
        tooltip: "Posted in the ledger.",
    },
    [ScheduleApplicationPeriodStatus.voided]: {
        label: "Voided",
        variant: "gray",
        tooltip: "Not recognized. No journal entry was posted.",
    },
    [ScheduleApplicationPeriodStatus.pending]: {
        label: "Pending",
        variant: "yellow",
        tooltip:
            "This month is due and has no journal entry. The recognition job skipped it because the books for this date were locked. Unlock the month to post.",
    },
};

const MILESTONE_PENDING_TOOLTIP = "Waiting for the milestone to be confirmed.";

const MILESTONE_PENDING_STATUS: ScheduleApplicationRowStatus = {
    label: "Pending",
    variant: "yellow",
    tooltip: MILESTONE_PENDING_TOOLTIP,
};

const MILESTONE_CONFIRMABLE_STATUS: ScheduleApplicationRowStatus = {
    ...MILESTONE_PENDING_STATUS,
    tooltip: `${MILESTONE_PENDING_TOOLTIP} Choose ${MILESTONE_ACHIEVED_MENU_LABEL} to recognize it.`,
};

const MILESTONE_PAUSED_STATUS: ScheduleApplicationRowStatus = {
    label: "Paused",
    variant: "yellow",
    tooltip: "On hold until you resume the schedule.",
};

const MILESTONE_SCHEDULE_PAUSED_STATUS: ScheduleApplicationRowStatus = {
    label: "Paused",
    variant: "yellow",
    tooltip: "No milestones are recognized until you resume.",
};

const LIFECYCLE_SUBTYPE_DISPLAY: Partial<
    Record<ScheduleJeSubtype, ScheduleApplicationRowStatus>
> = {
    [ScheduleJeSubtype.reversal]: {
        label: "Reversal",
        variant: "red",
        tooltip:
            "Cancels an earlier entry on the same date, with debit and credit swapped.",
    },
    [ScheduleJeSubtype.catchUp]: {
        label: "Catch-up",
        variant: "blue",
        tooltip:
            "One adjustment for the difference on months that were already earned.",
    },
    [ScheduleJeSubtype.addition]: {
        label: "Deferred",
        variant: "blue",
        tooltip:
            "Moves the scheduled amount onto the holding account. A later one adjusts the deferral after the amount changed.",
    },
    [ScheduleJeSubtype.recognized]: {
        label: "Accelerated",
        variant: "purple",
        tooltip: "The rest of the balance was recognized on the stop date.",
    },
    [ScheduleJeSubtype.reclassification]: {
        label: "Reclassified",
        variant: "purple",
        tooltip: "The rest of the balance was moved to another account.",
    },
};

const REVERSED_STATUS: ScheduleApplicationRowStatus = {
    label: "Reversed",
    variant: "red",
    tooltip: "Reversed by the entry below.",
};

const SCHEDULED_STATUS: ScheduleApplicationRowStatus = {
    label: "Scheduled",
    variant: "blue",
    tooltip:
        "Already in the ledger, dated in the future. On this date the status changes to Posted.",
};

/**
 * A recognition entry is written when the schedule is applied, including for
 * months that have not arrived. Until that date, the row reads as scheduled;
 * on the date it reads as posted. The stored period status does not change.
 */
function getPeriodRowStatus(
    row: ScheduleApplicationPeriodRow,
    today: string,
): ScheduleApplicationRowStatus {
    if (row.milestone && isMilestoneOnHold(row)) {
        return MILESTONE_PAUSED_STATUS;
    }
    if (
        row.milestone &&
        row.periodStatus === ScheduleApplicationPeriodStatus.pending
    ) {
        return canMarkMilestoneAchieved(row)
            ? MILESTONE_CONFIRMABLE_STATUS
            : MILESTONE_PENDING_STATUS;
    }
    if (row.isReversed) {
        return REVERSED_STATUS;
    }
    const stored = PERIOD_STATUS_DISPLAY[row.periodStatus];
    if (
        row.periodStatus === ScheduleApplicationPeriodStatus.posted &&
        row.date > today
    ) {
        return SCHEDULED_STATUS;
    }
    return stored;
}

export function getScheduleApplicationRowStatus(
    row: ScheduleApplicationRow,
    today: string = toIsoDate(getPSTDate()),
): ScheduleApplicationRowStatus | null {
    switch (row.rowType) {
        case "application": {
            if (row.status === ScheduleApplicationStatus.active) {
                return row.isModified
                    ? SCHEDULE_MODIFIED_STATUS
                    : SCHEDULE_ACTIVE_STATUS;
            }
            if (
                row.isMilestone &&
                row.status === ScheduleApplicationStatus.paused
            ) {
                return MILESTONE_SCHEDULE_PAUSED_STATUS;
            }
            return APPLICATION_STATUS_DISPLAY[row.status] ?? null;
        }
        case "period":
            return getPeriodRowStatus(row, today);
        case "lifecycleJe":
            return LIFECYCLE_SUBTYPE_DISPLAY[row.subtype] ?? null;
        default:
            return null;
    }
}

export const SCHEDULE_ROW_SOURCE_LABEL = "Schedule";

export function getScheduleApplicationSourceLabel(
    row: ScheduleApplicationRow,
): string | null {
    switch (row.rowType) {
        case "application":
            return SCHEDULE_SOURCE_TYPE_LABELS[row.sourceType];
        case "period":
        case "lifecycleJe":
            return SCHEDULE_ROW_SOURCE_LABEL;
        default:
            return null;
    }
}

/**
 * Row ⋯ verbs in the order the design lists them, filtered by what the
 * application's status allows.
 */
export function getAvailableScheduleLifecycleVerbs(row: {
    status: ScheduleApplicationStatus;
    isMilestone: boolean;
}): ScheduleLifecycleVerb[] {
    switch (row.status) {
        case ScheduleApplicationStatus.draft:
            return [ScheduleLifecycleVerb.delete];
        case ScheduleApplicationStatus.active:
            return [
                ...(row.isMilestone ? [] : [ScheduleLifecycleVerb.change]),
                ScheduleLifecycleVerb.pause,
                ScheduleLifecycleVerb.cancel,
                ScheduleLifecycleVerb.delete,
            ];
        case ScheduleApplicationStatus.paused:
            return [
                ScheduleLifecycleVerb.resume,
                ScheduleLifecycleVerb.cancel,
                ScheduleLifecycleVerb.delete,
            ];
        case ScheduleApplicationStatus.completed:
            return [
                ...(row.isMilestone ? [] : [ScheduleLifecycleVerb.change]),
                ScheduleLifecycleVerb.delete,
            ];
        case ScheduleApplicationStatus.stopped:
            return [ScheduleLifecycleVerb.delete];
        default:
            return [];
    }
}

export const SCHEDULE_LIFECYCLE_FILTER_STATUS_LABELS: Record<
    ScheduleLifecycleFilterStatus,
    string
> = {
    [ScheduleLifecycleFilterStatus.active]: "Active",
    [ScheduleLifecycleFilterStatus.modified]: "Changed",
    [ScheduleLifecycleFilterStatus.paused]: "Paused",
    [ScheduleLifecycleFilterStatus.stopped]: "Stopped",
    [ScheduleLifecycleFilterStatus.completed]: "Completed",
    [ScheduleLifecycleFilterStatus.unwound]: "Removed",
};
